<template>
  <LoadingDiv v-if="!isReady" />

  <form v-else class="rules-settings-form" @submit.prevent="handleSubmit">
    <section class="rules-form-section">
      <div class="rules-section-head">
        <span>Default Rules</span>
        <h5>Billing Defaults</h5>
        <p>Set the standard payment cycle used as the default when new policy rules are created.</p>
      </div>

      <div class="rules-field-grid">
        <div class="rules-field-card">
          <i class="material-icons">event_repeat</i>
          <div class="input-field">
            <input
              id="premiumCycle"
              type="number"
              v-model="premiumCycle"
              class="validate"
              required
            />
            <label class="active" for="premiumCycle">Payment Cycle (Months)</label>
          </div>
        </div>
      </div>
    </section>

    <section class="rules-form-section">
      <div class="rules-section-head">
        <span>Membership Rules</span>
        <h5>Principal & Family Limits</h5>
        <p>Maintain the default family and age limits used when policy-specific limits are not yet set.</p>
      </div>

      <div class="rules-field-grid rules-limit-grid">
        <div class="rules-field-card">
          <i class="material-icons">family_restroom</i>
          <div class="input-field">
            <input
              id="familySize"
              type="number"
              v-model="familySize"
              class="validate"
              required
            />
            <label class="active" for="familySize">Family Size (Total)</label>
          </div>
        </div>

        <div class="rules-field-card">
          <i class="material-icons">person</i>
          <div class="input-field">
            <input
              id="prinipalAge"
              type="number"
              v-model="principalAge"
              class="validate"
              required
            />
            <label class="active" for="prinipalAge">Principal Age (Max)</label>
          </div>
        </div>

        <div class="rules-field-card">
          <i class="material-icons">child_care</i>
          <div class="input-field">
            <input
              id="dependantAge"
              type="number"
              v-model="dependantAge"
              class="validate"
              required
            />
            <label class="active" for="dependantAge">Dependant Age (Max)</label>
          </div>
        </div>
      </div>
    </section>

    <div class="rules-note">
      <i class="material-icons">info</i>
      <span>These values act as defaults. Policy-specific settings may still apply where a client policy has its own current rules.</span>
    </div>

    <div class="rules-form-actions">
      <button
        class="waves-effect waves-light btn-large"
        :class="{ disabled: isSubmitting }"
      >
        <i class="material-icons white-text left">check_circle</i>
        {{ isSubmitting ? "Updating..." : "Save Rules" }}
      </button>
    </div>
  </form>
</template>
  
<script>
import { onMounted, ref } from "vue";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HmoComponent",
  components: { LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    // Workmen
    const response = ref([]);
    const error = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);

    // Rules table
    const premiumCycle = ref(null);
    const familySize = ref(null);
    const principalAge = ref(null);
    const dependantAge = ref(null);

    // start here
    onMounted(() => {
      // get items
      (async () => {
        const { res, err } = await getItems("rules");

        if (res.value) {
          // get
          response.value = res.value.data;

          // set
          response.value.forEach((x) => {
            if (x.setting == "premiumCycle" && premiumCycle.value == null) {
              // set
              premiumCycle.value = x.value;
            }

            if (x.setting == "familySize" && familySize.value == null) {
              // set
              familySize.value = x.value;
            }

            if (x.setting == "principalAge" && principalAge.value == null) {
              // set
              principalAge.value = x.value;
            }

            if (x.setting == "dependantAge" && dependantAge.value == null) {
              // set
              dependantAge.value = x.value;
            }
          });

          // is now Ready
          isReady.value = true;
        } else {
          error.value = err.value;
        }
      })();
    });

    // submit
    const handleSubmit = async () => {
      // start
      isSubmitting.value = true;
      error.value = false;

      // Materialize CSS handled all custome errors \\
      const newVal = [
        {
          setting: "premiumCycle",
          value: premiumCycle.value,
          scope: 0,
        },
        {
          setting: "familySize",
          value: familySize.value,
          scope: 0,
        },
        {
          setting: "principalAge",
          value: principalAge.value,
          scope: 0,
        },
        {
          setting: "dependantAge",
          value: dependantAge.value,
          scope: 0,
        },
      ];

      // post item
      const { res, err } = await postItem("rules", newVal);

      if (res.value) {
        // next
        // emit new parameters
        context.emit("next", {
          step: 6,
          isComplete: true,
        });
      } else {
        error.value = err.value;
      }

      // end here
      isSubmitting.value = false;
    };

    return {
      error,
      isReady,
      isSubmitting,
      handleSubmit,
      premiumCycle,
      familySize,
      principalAge,
      dependantAge,
    };
  },
};
</script>
  
<style scoped>
.rules-settings-form {
  display: grid;
  gap: 18px;
}

.rules-form-section,
.rules-note {
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
  overflow: hidden;
}

.rules-section-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 22px 24px;
}

.rules-section-head span {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.rules-section-head h5 {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.rules-section-head p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px 0 0;
}

.rules-field-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr);
  padding: 18px;
}

.rules-limit-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.rules-field-card {
  align-items: start;
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  display: grid;
  gap: 14px;
  grid-template-columns: 42px minmax(0, 1fr);
  padding: 16px 16px 6px;
}

.rules-field-card > i {
  align-items: center;
  background: #eef4f5;
  border-radius: 6px;
  color: #00897b;
  display: flex;
  font-size: 22px;
  height: 42px;
  justify-content: center;
  width: 42px;
  margin-top: 6px;
}

.rules-field-card :deep(.input-field) {
  margin-bottom: 0.55rem;
  margin-top: 0.65rem;
}

.rules-field-card :deep(.input-field label.active) {
  display: block;
  margin-bottom: 5px;
  position: static;
  transform: none;
}

.rules-field-card :deep(input) {
  color: #263238;
  font-weight: 800;
  margin-bottom: 0;
}

.rules-field-card :deep(label) {
  color: #607d8b;
  font-weight: 700;
}

.rules-note {
  align-items: center;
  background: #fbfcfc;
  color: #607d8b;
  display: flex;
  gap: 12px;
  padding: 16px 18px;
}

.rules-note i {
  color: #00897b;
}

.rules-note span {
  font-weight: 700;
  line-height: 1.45;
}

.rules-form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 18px;
}

.rules-form-actions .btn-large {
  background: #00897b;
  border-radius: 6px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

@media only screen and (max-width: 992px) {
  .rules-limit-grid {
    grid-template-columns: 1fr;
  }
}
</style>
